/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */

/**
 * Provides a marker interface for all APM Performance Monitors. Generally,
 * most Performance Monitors, such as those for Initialization, Page, and
 * Features will implement the `IPerformanceMonitor` derived interface.
 * However, not all monitors, such as those for Monitoring Crash events
 * require an `IPerformanceMonitor` implementation. As such, this interface
 * serves as the marker interface for all APM monitors.
 */
export interface IMonitor {
  readonly category: string;
  context: string;
}

/**
 * The `IPerformanceMonitor` interface defines the API for Performance Monitor
 * implementations, providing a unified API for performing monitoring of system
 * performance metrics across components and services within an application.
 */
export interface IPerformanceMonitor extends IMonitor {
  context: string;
  readonly autoComplete: boolean;
  isRunning(): boolean;
  start(context?: string, force?: boolean): boolean;
  startService(url: string): void;
  stopService(url: string): void;
  isServiceRunning(url: string): boolean;
  hasRunningServices(): boolean;
  hasService(url: string): boolean;
  stop(isAutoStop?: boolean): number;
  blackList(item: Blacklist | string, override?: boolean): void;
  isBlackListed(url: string): boolean;
}

/**
 * Defines the `Beacon` Client API which provides the API for sending APM events
 * to an external source or service.
 */
export interface IBeaconClient {
  send(eventName: string, payload: MonitorResults, category: MonitorCategory): boolean;
}

/**
 * Defines the valid Application Performance Monitoring Categories that can
 * be used for a specific Performance Monitor instance.
 *
 * The current categories are as follows:
 *  - `Initialization`: Used for application initialization performance monitoring.
 *  - `Page`: Used exclusively for individual page performance monitoring.
 *  - `Feature`: Used exclusively for individual feature performance monitoring.
 *  - `Crash`: Used exclusively for individual page crash event monitoring.
 */
export enum MonitorCategory {
  Page = 'Page',
  Initialization = 'Initialization',
  Feature = 'Feature',
  Crash = 'Crash'
}

/**
 * Defines a `Performance` Monitoring measuring result instance which captures
 * the start, end, and duration times of a given measurement.
 */
export type MeasurementResult = {
  start: number,
  end: number,
  duration: number
};

/**
 * Defines the collective service timing measurements whereby each key is a
 * service url that has been included in the monitoring, and each value is a
 * collection of `MeasurementResult`, each representing a particular service
 * invocations duration, start, and end times.
 */
export type ServiceTimings = {
  [url in string]: Partial<MeasurementResult>[]
};

/**
 * Provides the type which contains the service results from a Performance
 * Monitor instance. These results are generally used as part of the overall
 * results which are logged and / or emitted to an underlying APM platform.
 */
export type ServiceResults = {
  runningServices: string[],
  measuredServices: string[],
  parallelServiceTime: number;
  cumulativeServiceTime: number;
  serviceTimings: ServiceTimings;
}

/**
 * Provides the type which contains the results from a Performance Monitor
 * instance, which can be logged and / or emitted to an underlying APM
 * platform.
 */
export type MonitorResults = {
  totalTime: number;
  totalUITime: number;
  serviceTime: number;
  cumulativeServiceTime: number;
  duplications: string[];
  measuredServices: string[];
  serviceTimings: ServiceTimings;
  error?: Error;
}

/**
 * Provides a convenience type for mapping performance items.
 */
export type ItemsMap = {
  [key: string]: true;
}

/**
 * Convenience alias for Performance Monitor Service Blacklisted items
 */
export type Blacklist = ItemsMap;

/**
 * Convenience alias for Performance Monitor Service duplication items
 */
export type Duplications = ItemsMap;
